import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@app/api/ApiError';
import {
  openChannel,
  type OpenChannel,
  type ChannelEvent,
  type ChannelOptions,
} from './Converse.ws';
import { useTriggerCall } from './Converse.api';
import { resultRestSwap, type SwapInScreen } from './ConverseModel';

/**
 * A conversa de T25, pelos dois transportes.
 *
 * ## Por que dois
 *
 * O canal é o desenho (streaming, para quando o agente virar LLM); o REST é a
 * alternativa quando o upgrade não passa — proxy corporativo que não repassa
 * `Upgrade` é o caso comum, e numa demonstração no cliente não dá para descobrir
 * isso na hora. Os dois alimentam o **mesmo** `TrocaNaTela`, então a tela não
 * sabe por onde a resposta veio.
 *
 * ## Como os eventos casam com a troca certa
 *
 * O servidor serializa `salto…` → `resposta` por envio, então o alvo de um salto
 * é sempre a troca mais antiga **sem resposta**. A `decisao` vem minutos depois,
 * fora de ordem, e por isso casa por `request_id` — que já está na troca quando
 * ela chega.
 */

export type Transport = 'conectando' | 'canal' | 'rest';

export interface ConversationSend {
  pessoa: string;
  agente: string;
  /** Slug do especialista; vazio = chamada direta. */
  delegado: string;
  tool: string;
  /** UUID de quem vai executar — resolvido pela tela, para o simulador. */
  identityId: string | null;
}

export interface Conversation {
  trocas: SwapInScreen[];
  transporte: Transport;
  enviar: (envio: ConversationSend) => void;
  /** Erro do último envio (o canal ou o REST recusaram). */
  error: ApiError | null;
  limparErro: () => void;
  /** `true` enquanto um envio está em voo. */
  enviando: boolean;
}

/**
 * O evento `erro` do canal, como `ApiError`.
 *
 * O canal e as rotas REST usam a **mesma** taxonomia de `code` (é o mesmo caso de
 * uso por trás das duas), então o error vira o mesmo tipo e a tela tem um caminho
 * só até o banner — que decide pelo `code`, nunca pela message.
 *
 * `status: 0` porque não há resposta HTTP: a recusa chegou como quadro do canal.
 * Inventar o status que a rota REST teria devolvido seria afirmar sobre uma
 * resposta que não existiu, e nada na tela lê esse campo.
 */
function channelError(codigo: string, message: string): ApiError {
  return new ApiError({ code: codigo, message: message, status: 0 });
}

export interface ConversationOptions {
  /** Injeta o construtor de WebSocket nos testes. */
  criarCanal?: ChannelOptions['criar'];
}

export function useConversation(opcoes: ConversationOptions = {}): Conversation {
  const [swaps, setTrocas] = useState<SwapInScreen[]>([]);
  const [transport, setTransporte] = useState<Transport>('conectando');
  const [error, setErro] = useState<ApiError | null>(null);
  const [enviando, setEnviando] = useState(false);

  const canal = useRef<OpenChannel | null>(null);
  const rest = useTriggerCall();

  // `criarCanal` entra por ref para o efeito de abertura não depender dela: uma
  // função recriada a cada render reabriria o canal a cada render.
  const createChannel = useRef(opcoes.criarCanal);
  createChannel.current = opcoes.criarCanal;

  const apply = useCallback((event: ChannelEvent) => {
    setTrocas((anteriores) => applyEvent(anteriores, event));
    if (event.kind === 'resposta') setEnviando(false);
    if (event.kind === 'erro') {
      setErro(channelError(event.code, event.message));
      setEnviando(false);
    }
  }, []);

  useEffect(() => {
    const open = openChannel({
      criar: createChannel.current,
      onAbrir: () => setTransporte('canal'),
      onEvento: apply,
      onFechar: (abriu) => {
        canal.current = null;
        // Canal que **nunca** abriu é upgrade recusado: cai para REST. Canal que
        // abriu e caiu depois também — reconectar sozinho reenviaria nada, mas
        // deixaria a tela num estado em que o próximo envio falha calado.
        setTransporte('rest');
        if (abriu) setEnviando(false);
      },
    });
    canal.current = open;
    return () => open.fechar();
  }, [apply]);

  const send = useCallback(
    (envio: ConversationSend) => {
      const desde = new Date().toISOString();
      const chave = `${desde}|${envio.pessoa}|${envio.agente}|${envio.tool}`;
      setErro(null);
      setEnviando(true);

      if (canal.current && transport === 'canal') {
        setTrocas((anteriores) => [...anteriores, swapEmpty(chave, envio, desde)]);
        canal.current.enviar(
          envio.delegado
            ? {
                kind: 'delegar',
                generalist: envio.agente,
                agent: envio.delegado,
                person: envio.pessoa,
                tool: envio.tool,
              }
            : { kind: 'enviar', agent: envio.agente, person: envio.pessoa, tool: envio.tool },
        );
        return;
      }

      rest.mutate(
        { agente: envio.agente, delegado: envio.delegado, pessoa: envio.pessoa, tool: envio.tool },
        {
          onSuccess: (resultado) => {
            setTrocas((anteriores) => [
              ...anteriores,
              resultRestSwap({ chave, resultado, identityId: envio.identityId, desde }),
            ]);
            setEnviando(false);
          },
          onError: (falha) => {
            setErro(falha);
            setEnviando(false);
          },
        },
      );
    },
    [rest, transport],
  );

  return {
    trocas: swaps,
    transporte: transport,
    enviar: send,
    error,
    limparErro: useCallback(() => setErro(null), []),
    enviando,
  };
}

function swapEmpty(chave: string, envio: ConversationSend, desde: string): SwapInScreen {
  return {
    chave,
    pessoa: envio.pessoa,
    agente: envio.agente,
    delegadoA: envio.delegado || null,
    tool: envio.tool,
    saltos: [],
    resposta: null,
    decisao: { estado: 'aguardando' },
    identityId: envio.identityId,
    desde,
  };
}

/**
 * Aplica um evento à lista de trocas.
 *
 * Pura, e exportada, porque é a regra que decide **qual** troca cada evento
 * altera — a parte que erra em silêncio se errar: um `decisao` no lugar errado
 * põe o veredito de uma chamada sobre outra, e a tela não teria como acusar.
 */
export function applyEvent(swaps: SwapInScreen[], event: ChannelEvent): SwapInScreen[] {
  switch (event.kind) {
    case 'salto': {
      const alvo = pendingIndex(swaps);
      if (alvo < 0) return swaps;
      return swaps.map((swap, i) =>
        i === alvo
          ? {
              ...swap,
              saltos: [
                ...swap.saltos,
                {
                  step: event.step,
                  ok: event.ok,
                  detail: event.detail,
                  duration_ms: event.duration_ms,
                  // O tri-estado vem do servidor pelos dois transportes. Sem ele
                  // aqui, a trilha do canal cairia na leitura antiga (`ok` puro) e
                  // desenharia um DENY como caminho quebrado — só pelo canal, que
                  // é o transporte padrão da tela.
                  situation: event.situation,
                },
              ],
            }
          : swap,
      );
    }
    case 'resposta': {
      const alvo = pendingIndex(swaps);
      if (alvo < 0) return swaps;
      return swaps.map((swap, i) =>
        i === alvo
          ? {
              ...swap,
              // O executor é a fonte sobre quem atendeu: numa delegação a tela
              // conhecia o alvo pedido, e é a resposta que confirma quem executou.
              agente: event.agent || swap.agente,
              delegadoA: event.delegated_to || null,
              resposta: {
                statusHttp: event.http_status,
                body: event.body ?? null,
                requestId: event.request_id,
                duracaoMs: event.duration_ms,
              },
            }
          : swap,
      );
    }
    case 'decisao': {
      // Casa pelo `request_id` **da chamada**, que o evento carrega mesmo em
      // `nao_encontrada`: a espera dura minutos e o evento chega fora de ordem,
      // com outras trocas na frente. Casar pela «mais antiga esperando» poria o
      // veredito de uma chamada sobre outra, e a tela não teria como acusar.
      const alvo = swaps.findIndex((swap) => swap.resposta?.requestId === event.request_id);
      if (alvo < 0) return swaps;
      return swaps.map((swap, i) =>
        i === alvo
          ? {
              ...swap,
              decisao:
                event.verdict === 'nao_encontrada'
                  ? { estado: 'nao_encontrada' }
                  : {
                      estado: 'encontrada',
                      // O `request_id` do **log**, quando o gateway gerou o seu;
                      // é ele que abre a linha certa em T17. O da chamada só
                      // casou o evento com esta troca.
                      requestId: event.decision_request_id || event.request_id,
                      veredito: event.verdict,
                    },
            }
          : swap,
      );
    }
    case 'erro': {
      // A troca que não chegou a ter resposta sai da conversa: o motivo vira o
      // banner da tela, o mesmo do caminho REST.
      const alvo = pendingIndex(swaps);
      return alvo < 0 ? swaps : swaps.filter((_, i) => i !== alvo);
    }
    default:
      return swaps;
  }
}

/** A troca mais antiga ainda sem resposta — o alvo dos eventos de um envio. */
function pendingIndex(swaps: SwapInScreen[]): number {
  return swaps.findIndex((swap) => swap.resposta === null);
}
